<template>
    <v-dialog v-model="dialog" max-width="900" @click:outside="hide">
        <v-card class="card-container">
            <v-card-title class="title-container">
                <v-icon color="#FFFFFF" @click="hide" large class="mr-3">mdi-close-circle-outline</v-icon>
                {{ $t('routines.assign_routine') }}
            </v-card-title>
            <v-card-text class="mt-3">
                <v-row dense>
                    <v-col cols="12" md="6">
                        <set-selection-routine :coach-id="coachId" @change="onChangeSelection"
                            @collection="collection = $event" />
                        <v-alert type="warning" border="left" outlined dense class="mt-3">
                            {{ $t('routines.alert_replace_routines') }}
                        </v-alert>
                    </v-col>
                    <v-col cols="12" md="6" class="text-center">
                        <div class="subtitle-text">
                            {{ $t('routines.select_days_to_assign') }}
                        </div>
                        <mini-calendar :value="dates" @change="dates = $event" />
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions class="px-0 pb-0">
                <v-btn block depressed x-large color="primary" class="normal-btn no-radius black--text"
                    :disabled="disabled" @click="assign" :loading="loading">
                    {{ $t('common.assign') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import { db, FieldValue } from "@/utils/Firebase";
import AssignRoutineSelector from "@/components/coach/routines/AssignRoutineSelector";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import SetSelectionRoutine from './SetSelectionRoutine.vue';
import MiniCalendar from "@/components/common/MiniCalendar";
import {
    addAssignedRoutinesGroup,
    deleteAssignedRoutineStudentInDate,
    getPayloadBaseAssignedRoutine
} from "@/utils/Exercises";

export default {
    name: "AssignRoutineDialog",
    components: { KimsaCombobox, AssignRoutineSelector, SetSelectionRoutine, MiniCalendar },
    props: {
        routineId: {
            type: String,
            default: null,
        },
        coachId: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            dialog: false,
            routine: null,
            loading: false,
            currentSelection: null,
            collection: null,
            dates: [],
        }
    },
    computed: {
        ...mapState(['dialogActive']),
        disabled(vm = this) {
            return !vm.currentSelection || vm.dates.length === 0 || !vm.routine
        },
    },
    methods: {
        ...mapMutations(['setDialogActive']),
        async show() {
            const vm = this
            vm.dialog = true
            vm.setDialogActive(true)
            await vm.loadRoutine()
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.setDialogActive(false)
            vm.reset()
        },
        reset() {
            const vm = this
            vm.routine = null
            vm.valid = false
        },
        async loadRoutine() {
            let vm = this
            if (!vm.routineId) return
            let routineRef = await db.collection('routines').doc(vm.routineId).get()
            vm.routine = { ...routineRef.data(), id: routineRef.id }
        },
        onChangeSelection(selection) {
            const vm = this;
            vm.currentSelection = selection
        },
        async assign() {
            const vm = this
            vm.loading = true
            let index = 0
            for (let date of vm.dates) {
                let dateFormatted = vm.$moment(date).format('DD-MM-YYYY')

                let routineName = `${vm.currentSelection.name} - ${dateFormatted}`
                let payload = getPayloadBaseAssignedRoutine(routineName, date, vm.routine, vm.routine.blocks)

                if (vm.currentSelection.objectID) {
                    payload.studentId = vm.currentSelection.objectID
                    await deleteAssignedRoutineStudentInDate(vm.currentSelection.objectID, date)
                } else {
                    payload.groupId = vm.currentSelection.id
                    payload.teamId = vm.currentSelection.teamId || null
                }

                let assignedRoutineCreated = await db.collection(vm.collection).add(payload)

                if (vm.collection === 'groupAssignedRoutines') {
                    for (let student of vm.currentSelection?.students || []) {
                        await deleteAssignedRoutineStudentInDate(student, date)
                    }
                    await addAssignedRoutinesGroup({ id: assignedRoutineCreated.id, ...payload, date: date }, vm.currentSelection.students)
                }
                index++
            }

            await db.collection('routines').doc(vm.routine.id).update({ used: FieldValue.increment(index) })

            vm.loading = false
            vm.hide()
            vm.toast(vm.$t('routines.assigned_routines_successfully'))
        },
    },
    watch: {
        dialogActive(val) {
            if (!val) this.hide()
        },
        routineId() {
            this.loadRoutine()
        },
    }
}
</script>

<style scoped>
.card-container {
    border-radius: 8px;
    overflow: hidden;
}

.title-container {
    color: #FFFFFF;
    background: var(--v-backGroundPrimary-base);
}

::v-deep .v-date-picker-table {
    border-bottom: none;
}
</style>